import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import AccountCircle from "@mui/icons-material/AccountCircle";

import useFetch from "utils/fetch/useFetch";
import { asTitle } from "utils/Utils";

import AuthenticationClient from "authentication/AuthenticationClient";
import { useAuth } from "authentication/AuthProvider";
import { UserRole } from "authentication/Principal";

export default function UserMenu(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { call } = useFetch();
	const { principal, getRefreshToken, removeTokens } = useAuth();

	const [anchor, setAnchor] = useState<HTMLElement>();

	const logoutUser = useCallback(
		async (event: React.MouseEvent<HTMLLIElement>): Promise<void> => {
			event.preventDefault();
			if (principal === undefined || principal === null) {
				return;
			}

			const token = getRefreshToken();
			if (token == null) {
				navigate("/login");
				return;
			}

			call(AuthenticationClient.logout(token))
				.catch(() => null)
				.finally(removeTokens);
		},
		[principal, getRefreshToken]
	);

	const logoutUserEverywhere = useCallback(
		async (event: React.MouseEvent<HTMLLIElement>): Promise<void> => {
			event.preventDefault();
			if (principal === undefined || principal === null) {
				return;
			}

			const token = getRefreshToken();
			if (token == null) {
				navigate("/login");
				return;
			}

			call(AuthenticationClient.logoutAll())
				.catch(() => null)
				.finally(removeTokens);
		},
		[principal, getRefreshToken]
	);

	// function openProfilePage(
	// 	event: React.MouseEvent<HTMLLIElement, MouseEvent>
	// ): void {
	// 	event.preventDefault();
	// 	history("/profile");
	// }

	const openOrganisationPage = useCallback((): void => {
		if (principal?.hasOrganisation()) {
			navigate(`/organisations/${principal.organisationId}`);
		}
	}, [principal?.organisationId]);

	const openUserMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>): void => {
			event.preventDefault();
			setAnchor(event.currentTarget);
		},
		[]
	);

	const closeUserMenu = useCallback((): void => {
		setAnchor(undefined);
	}, []);

	return (
		<>
			<IconButton
				aria-label="user-icon"
				aria-controls="menu-appbar"
				aria-haspopup="true"
				onClick={openUserMenu}
				color="inherit"
				size="large">
				<AccountCircle />
			</IconButton>
			<Menu
				aria-label="user-menu"
				anchorEl={anchor}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
				open={anchor !== undefined}
				onClose={closeUserMenu}>
				{/* <MenuItem id="user-profile" onClick={openProfilePage}>
					{asTitle(t("profile"))}
				</MenuItem>
				<Divider /> */}

				{principal?.userRole === UserRole.OWNER && (
					<MenuItem onClick={openOrganisationPage}>
						{asTitle(t("organisation"))}
					</MenuItem>
				)}
				{principal?.userRole === UserRole.OWNER && <Divider />}
				<MenuItem id="user-logout" onClick={logoutUser}>
					{asTitle(t("logout"))}
				</MenuItem>
				<MenuItem id="user-logout-all" onClick={logoutUserEverywhere}>
					{asTitle(t("logoutAll"))}
				</MenuItem>
			</Menu>
		</>
	);
}
